import React, { useEffect, useState } from "react";
import InputField from "./InputField";
import {
  PAYMENT_METHODS,
  isBankWithdrawTab,
  isUpiWithdrawTab,
  isCryptoWithdrawTab,
  API_PAYMENT_METHODS,
  AddAccountFormType,
  AddCryptoWalletFormType,
  AddUpiAccountFormType,
  WithdrawFormType,
  addAccountFormArr,
  addCryptoWalletFormArr,
  addUpiFormArr,
  cryptoCurrencyList,
  withdrawFormArr,
  useAppSelector,
  PaymentMethodObjType,
  JwtToken,
  getFieldFromToken,
  toProviderPath,
} from "@hypexone/uicore";
import Button from "../../components/shared/Button/Button";
import {  paymentClient } from "../../../App.utils";
import { CurrencyListDto } from "../Payments/commonutils";


interface WithdrawFormFieldsProps {
  withdrawFormData: WithdrawFormType;
  handleWithdrawFormChange: React.ChangeEventHandler<HTMLInputElement>;
  onSave: (e: React.FormEvent<HTMLFormElement>) => void;
  isWithdrawSaveBtnDisabled: boolean;
  loading: boolean;
  getReceivableAmount: Function;
  receivableAmount: number;
  currentSelectedAccount: PaymentMethodObjType;
  paymentProvider?: string;
}

export const WithdrawFormFields: React.FC<WithdrawFormFieldsProps> = ({
  withdrawFormData,
  handleWithdrawFormChange,
  isWithdrawSaveBtnDisabled,
  onSave,
  loading,
  getReceivableAmount,
  receivableAmount,
  currentSelectedAccount,
  paymentProvider,
}) => {
  const { langData } = useAppSelector((state) => state.cms);
  return (
    <form
      onSubmit={onSave}
      className="flex flex-col mt-4 md:ml-4 w-full md:w-[50%] text-base leading-none text-black"
    >
      <div className="text-xs text-red-500 mb-4">
        Note : Please allow 72hrs for withdrawal amount to credit, in case of
        any further delay reach out to customer care.
      </div>
      {withdrawFormArr.map((field) => {
        const { key, label, langKey, placeholder, type } = field;
        return (
          <React.Fragment key={key}>
            <InputField
              label={langData?.[langKey]}
              key={label}
              value={withdrawFormData[key]}
              id={key}
              type={type}
              placeholder={langData?.[placeholder]}
              onChange={handleWithdrawFormChange}
              disabled={receivableAmount !== null}
            />
          </React.Fragment>
        );
      })}
     


      {(paymentProvider?.toLowerCase() === "zenpaycrypto" || paymentProvider?.toLowerCase() === "zenpaycryptoseamless") ? (
        <>
          {!receivableAmount && (
            <Button
              type="button"
              className={`gap-2.5 px-4 py-2 mt-6 text-base text-center text-white ${!isWithdrawSaveBtnDisabled ? "bg-blue-600" : "bg-blue-400"} rounded border border-blue-600 border-solid min-h-[38px]`}
              loading={loading}
              onClick={() => getReceivableAmount()}
            >
              {langData?.["next"]}
            </Button>
          )}

          {receivableAmount && (
            <>
              <div className="p-2 border bg-slate-300 my-3 rounded text-center">
                {langData?.["receivable_crypto_amount"]}: {receivableAmount + " " + currentSelectedAccount?.queueMethodDetails?.cryptoCurrency}
              </div>
              <Button
                type="submit"
                className={`gap-2.5 px-4 py-2 mt-2 text-base text-center text-white ${!isWithdrawSaveBtnDisabled ? "bg-blue-600" : "bg-blue-400"} rounded border border-blue-600 border-solid min-h-[38px]`}
                loading={loading}
              >
                {langData?.["submit"]}
              </Button>
            </>
          )}
        </>
      ) :
        <>
          <Button
            type="submit"
            className={`gap-2.5 px-4 py-2 mt-2 text-base text-center text-white ${!isWithdrawSaveBtnDisabled ? "bg-blue-600" : "bg-blue-400"} rounded border border-blue-600 border-solid min-h-[38px]`}
            loading={loading}
          >
            {langData?.["submit"]}
          </Button>
        </>
      }
    </form>
  );
};

interface AddAccountFieldsProps {
  resetForms: () => void;
  onSave: (e: React.FormEvent<HTMLFormElement>) => void;
  addAccountFormData: AddAccountFormType;
  handleAddAccountFormChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  isSaveBtnDisabled: boolean;
  handleAddUpiAccountFormChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  addUpiAccountFormData: AddUpiAccountFormType;
  selectedPaymentMethod: PAYMENT_METHODS | API_PAYMENT_METHODS;
  addCryptoWalletFormData: AddCryptoWalletFormType;
  setAddCryptoWalletFormData: any;
  loading: boolean;
  otpLoader: boolean;
  otpTimer: number;
  phoneNumbeErrorMsg: string;
  sendOtp: () => void;
  selectedPaymentProvider?: string;
}

export const AddAccountFormFields: React.FC<AddAccountFieldsProps> = ({
  addAccountFormData,
  addUpiAccountFormData,
  handleAddAccountFormChange,
  handleAddUpiAccountFormChange,
  isSaveBtnDisabled,
  onSave,
  resetForms,
  selectedPaymentMethod,
  addCryptoWalletFormData,
  setAddCryptoWalletFormData,
  loading,
  otpLoader,
  otpTimer,
  phoneNumbeErrorMsg,
  sendOtp,
  selectedPaymentProvider
}) => {
  const { langData } = useAppSelector((state) => state.cms);
  const [currencyList, setCurrencyList] = useState<CurrencyListDto[]>([]);
  const isOnlineUser = getFieldFromToken(JwtToken.MODE)?.toLowerCase() === 'online';

  const handleSelectFieldChange = (event: any) => {
    const selectedValue = event.target.value;
    const selected = currencyList.find(
      (crypto) => crypto.crypto_currency + crypto.blockchain === selectedValue
    );
    addCryptoWalletFormData.selectedCryptoCurrency = selected;
    setAddCryptoWalletFormData({ ...addCryptoWalletFormData });
  };

  const handleAddCryptoWalletFormChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    if (event.target.tagName === "SELECT") {
      handleSelectFieldChange(event);
    } else {
      addCryptoWalletFormData[event.target.id] = event.target.value;
      setAddCryptoWalletFormData({ ...addCryptoWalletFormData });
    }
  };

  const getCurrencyList = async () => {
    try {
      const response = await paymentClient.getCurrencyList(
        toProviderPath(selectedPaymentProvider ?? "")
      );
      if (response.status === 200) {
        setCurrencyList(response?.data?.crypto_currency);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (isCryptoWithdrawTab(selectedPaymentMethod)) {
      getCurrencyList();
    }
  }, []);

  return (
    <form
      onSubmit={onSave}
      className="flex flex-col mt-4 md:ml-4 w-full md:w-[50%] text-base leading-none text-black"
    >
      {isBankWithdrawTab(selectedPaymentMethod) &&
        addAccountFormArr.map((field) => {
          const { key, label, langKey, placeholder, type } = field;
          return (
            <React.Fragment key={key}>
              <InputField
                label={langData?.[langKey]}
                key={label}
                value={addAccountFormData[key]}
                id={key}
                type={type}
                placeholder={langData?.[placeholder]}
                onChange={handleAddAccountFormChange}
              />
            </React.Fragment>
          );
        })}
      {isUpiWithdrawTab(selectedPaymentMethod) &&
        addUpiFormArr.map((field) => {
          const { key, label, langKey, placeholder, type } = field;
          return (
            <React.Fragment key={key}>
              <InputField
                label={langData?.[langKey]}
                key={label}
                value={addUpiAccountFormData[key]}
                id={key}
                type={type}
                placeholder={langData?.[placeholder]}
                onChange={handleAddUpiAccountFormChange}
              />
            </React.Fragment>
          );
        })}
      {isCryptoWithdrawTab(selectedPaymentMethod) && (
        <>
          <div>
            <label htmlFor={"crypto-select"} className="self-start">
              {langData?.["select_currency"]}
            </label>
            <select
              id="crypto-select"
              className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2"
              onChange={handleAddCryptoWalletFormChange}
              defaultValue={
                addCryptoWalletFormData?.selectedCryptoCurrency
                  ? addCryptoWalletFormData.selectedCryptoCurrency
                    .crypto_currency
                  : ""
              }
            >
              <option value="" disabled>
                {langData?.["select_a_crypto"]}
              </option>
              {currencyList.map((crypto, index) => {
                return (
                  <option
                    key={index}
                    value={crypto.crypto_currency + crypto.blockchain}
                  >
                    {crypto.crypto_currency + " (" + crypto.blockchain + ")"}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="mt-3">
            {addCryptoWalletFormArr.map((field) => {
              const { key, label, langKey, placeholder, type } = field;
              return (
                <React.Fragment key={key}>
                  <InputField
                    label={langData?.[langKey]}
                    key={label}
                    value={addCryptoWalletFormData[key]}
                    id={key}
                    type={type}
                    placeholder={langData?.[placeholder]}
                    onChange={handleAddCryptoWalletFormChange}
                  />
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}
      {isOnlineUser &&
        <div className="flex flex-row items-end gap-4 mt-2">
          <InputField
            label={langData?.["otp"]}
            key={"otp"}
            value={isBankWithdrawTab(selectedPaymentMethod) ? addAccountFormData["otp"] : isUpiWithdrawTab(selectedPaymentMethod) ? addUpiAccountFormData["otp"] : addCryptoWalletFormData["otp"]}
            id={"otp"}
            type={"number"}
            placeholder={langData?.["otp"]}
            onChange={isBankWithdrawTab(selectedPaymentMethod) ? handleAddAccountFormChange : isUpiWithdrawTab(selectedPaymentMethod) ? handleAddUpiAccountFormChange : handleAddCryptoWalletFormChange}
            width="w-[50%]"
          />
          <div className="mb-2">
            <Button
              className={`gap-2.5 px-4 py-2 text-base text-center text-white ${!otpLoader ? "bg-slate-700" : "bg-slate-500"} rounded min-h-[38px]`}
              loading={otpLoader}
              onClick={sendOtp}
              disabled={otpLoader || (otpTimer > 0)}
            >
              {langData?.['send_otp']}
            </Button>
            {otpTimer > 0 && (
              <div className="text-red-500 text-sm">
                {langData?.['resend_in_txt'] + ' ' + otpTimer + 's'}
              </div>
            )}
            {phoneNumbeErrorMsg && (
              <div className="text-red-500 text-sm">{phoneNumbeErrorMsg}</div>
            )}
          </div>
        </div>
      }
      <Button
        type="submit"
        className={`gap-2.5 self-stretch px-4 py-2 mt-6 text-base text-center text-white ${!isSaveBtnDisabled ? "bg-blue-600" : "bg-blue-400"
          } rounded border border-blue-600 border-solid min-h-[38px]`}
        loading={loading}
      >
        {langData?.["save_changes"]}
      </Button>
    </form>
  );
};
